/**
 * The grade report: the rubric, the findings with the lesson behind the first
 * one, the AI-gradable block, and save/load.
 *
 * This is the only place in the app that passes judgment, and it only opens
 * when the student asks it to.
 */

import { useRef, useState, type JSX } from 'react';
import { TIPS, buildBlock, buildLesson, lessonFilename, parseLesson } from '../core';
import { useSession } from '../state/store';
import { Modal } from './Modal';

export function GradeDialog({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const exercise = useSession((s) => s.exercise);
  const report = useSession((s) => s.report);
  const assisted = useSession((s) => s.assisted);
  const meta = useSession((s) => s.meta);
  const loadExercise = useSession((s) => s.loadExercise);
  const fileInput = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  if (!report) return <Modal open={false} title="Grade" onClose={onClose} children={null} />;

  const block = buildBlock(exercise, meta(), report);
  const findings = report.analysis.findings;
  const leadFinding = findings.find((f) => f.severity === 'error') ?? findings[0];
  const empty = exercise.cp.filter((d) => d == null).length;
  const anyAssist = assisted.labels || assisted.allowed;

  const save = () => {
    const data = buildLesson(exercise, meta(), report);
    const blob = new Blob([JSON.stringify(data, null, 1)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = lessonFilename(exercise);
    a.click();
    URL.revokeObjectURL(url);
  };

  const load = (file: File) => {
    void file.text().then((text) => {
      const parsed = parseLesson(JSON.parse(text));
      if (!parsed) {
        window.alert('That does not look like a lesson file.');
        return;
      }
      loadExercise(parsed.exercise, {
        profile: parsed.meta.profile,
        layout: parsed.meta.singleStaff ? 'single' : 'grand',
      });
      onClose();
    });
  };

  return (
    <Modal
      open={open}
      title="Grade"
      onClose={onClose}
      wide
      footer={
        <>
          <button
            type="button"
            className="button"
            onClick={() => {
              void navigator.clipboard?.writeText(block).then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1600);
              });
            }}
          >
            {copied ? 'Copied' : 'Copy block'}
          </button>
          <button type="button" className="button" onClick={save}>
            Save lesson
          </button>
          <button type="button" className="button" onClick={() => fileInput.current?.click()}>
            Load lesson
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".json,application/json"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) load(file);
              e.target.value = '';
            }}
          />
        </>
      }
    >
      <div className="grade__score">
        <span className="grade__total">{report.complete ? `${report.total} / ${report.max}` : `— / ${report.max}`}</span>
        <span className="grade__note">
          {report.complete
            ? anyAssist
              ? 'the assisted rows were helped by an assist you had switched on'
              : 'cold run — no assists were engaged'
            : `${empty} measure${empty === 1 ? '' : 's'} still resting — the score is withheld until the line is complete`}
        </span>
      </div>

      <table className="grade__table">
        <tbody>
          {report.dims.map((d) => {
            const wasAssisted = d.assists.some((a) => assisted[a]);
            return (
              <tr key={d.name}>
                <td>
                  {d.name}
                  {wasAssisted && <span className="badge">assisted</span>}
                </td>
                <td>
                  {d.got} / {d.max}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h3>Findings</h3>
      {findings.length === 0 ? (
        <p className="finding finding--ok">Clean — no rule violations.</p>
      ) : (
        <ul className="findings">
          {findings.map((f, i) => (
            <li key={i} className={`finding finding--${f.severity}`}>
              {f.message}
            </li>
          ))}
        </ul>
      )}
      {leadFinding && <p className="grade__tip" dangerouslySetInnerHTML={{ __html: markdownish(TIPS[leadFinding.code] ?? TIPS.IDLE) }} />}

      <h3>AI-gradable block</h3>
      <p className="field__note">
        The arithmetic is already done here, so a language model can judge the <em>style</em> — the arc of the line,
        whether it sings — without recounting a single interval.
      </p>
      <pre className="block">{block}</pre>
    </Modal>
  );
}

/**
 * The tip texts carry `**bold**` markers rather than HTML, so the strings stay
 * readable in the source. This turns them into the one tag they use.
 */
function markdownish(text: string): string {
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
}
